<template>
  <el-form :model="form" :rules="rules" ref="bridgeForm" label-width="110px">
    <el-row>
      <el-col :span="24">
        <el-form-item label="桥梁名称" prop="bridgeName" class="is-required">
          <el-input size="small" v-model="form.bridgeName" placeholder="请输入桥梁名称"></el-input>
        </el-form-item>
      </el-col>
    </el-row>
    
    <el-row>
      <el-col :span="24">
        <el-form-item label="桥梁编号" prop="bridgeCode" class="is-required">
          <el-input size="small" v-model="form.bridgeCode" placeholder="系统自动生成" disabled></el-input>
        </el-form-item>
      </el-col>
    </el-row>
    
    <el-row>
      <el-col :span="24">
        <el-form-item label="桥梁类型" prop="bridgeType" class="is-required">
          <el-select size="small" v-model="form.bridgeType" placeholder="请选择桥梁类型" style="width: 100%;">
            <el-option
              v-for="item in actualBridgeTypeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-select>
        </el-form-item>
      </el-col>
    </el-row>
    
    <el-row>
      <el-col :span="24">
        <el-form-item label="建成时间" prop="buildTime" class="is-required">
          <el-date-picker
            size="small"
            v-model="form.buildTime"
            type="date"
            placeholder="请选择建成时间"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            style="width: 100%;"
            :picker-options="{
              disabledDate(time) {
                return time.getTime() > Date.now();
              }
            }">
          </el-date-picker>
        </el-form-item>
      </el-col>
    </el-row>
    
    <el-row>
      <el-col :span="24">
        <el-form-item label="经纬度" class="is-required">
          <div class="coord-inputs" style="width:100%;">
            <el-input 
              size="small"
              :value="(form.longitude && form.latitude) ? 
                `${formatLongitude(form.longitude)}  ,  ${formatLatitude(form.latitude)}` : ''"
              placeholder="点击左侧按钮开始标记位置" 
              readonly
              disabled>
              <el-button slot="prepend" @click="$emit('toggle-location-picking')" 
                :class="{ 'active-location-btn': isLocationPickingActive }" 
                size="small" 
                title="标记位置">
                <i class="el-icon-location"></i>
              </el-button>
            </el-input>
          </div>
          <p class="coord-hint" v-if="isLocationPickingActive">点击地图标记桥梁位置</p>
          <p class="coord-hint" v-else>点击左侧定位按钮开始标记位置</p>
        </el-form-item>
      </el-col>
    </el-row>
    
    <el-row>
      <el-col :span="24">
        <el-form-item label="空高校准(m)" prop="clearanceHeight" class="is-required">
          <el-input size="small" v-model.number="form.clearanceHeight" type="number" placeholder="请输入空高校准值"></el-input>
        </el-form-item>
      </el-col>
    </el-row>
    
    <el-row>
      <el-col :span="24">
        <el-form-item label="预警高度(m)" prop="warningHeight" class="is-required">
          <el-input size="small" v-model.number="form.warningHeight" type="number" placeholder="请输入预警高度"></el-input>
        </el-form-item>
      </el-col>
    </el-row>
    
    <el-row>
      <el-col :span="24">
        <el-form-item label="预警速度(kn)" prop="warningSpeed" class="is-required">
          <el-input size="small" v-model.number="form.warningSpeed" type="number" placeholder="请输入预警速度"></el-input>
        </el-form-item>
      </el-col>
    </el-row>
    
    <el-row>
      <el-col :span="24">
        <el-form-item label="结构信息" prop="structureInfo">
          <el-input 
            size="small"
            type="textarea" 
            :rows="3" 
            placeholder="请输入桥梁结构信息" 
            v-model="form.structureInfo">
          </el-input>
        </el-form-item>
      </el-col>
    </el-row>
    
    <el-row>
      <el-col :span="24">
        <el-form-item label="状态" prop="status" class="is-required">
          <el-radio-group size="small" v-model="form.status">
            <el-radio :label="1">正常</el-radio>
            <el-radio :label="0">异常</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-col>
    </el-row>
  </el-form>
</template>

<script>
import { formatLatitude, formatLongitude } from '@/utils/coordinate';

export default {
  name: 'BridgeForm',
  props: {
    // 桥梁数据
    value: {
      type: Object,
      required: true
    },
    // 是否为编辑模式
    isEdit: {
      type: Boolean,
      default: false
    },
    // 是否正在选择位置
    isLocationPickingActive: {
      type: Boolean,
      default: false
    },
    // 桥梁类型选项
    bridgeTypeOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      form: { ...this.value },
      // 本地桥梁类型选项，当外部未提供时使用
      localBridgeTypeOptions: [
        { value: '斜拉桥', label: '斜拉桥' },
        { value: '悬索桥', label: '悬索桥' },
        { value: '拱桥', label: '拱桥' },
        { value: '梁桥', label: '梁桥' },
        { value: '钢架桥', label: '钢架桥' },
        { value: '其它', label: '其它' }
      ],
      // 表单验证规则
      rules: {
        bridgeName: [
          { required: true, message: '请输入桥梁名称', trigger: 'blur' },
          { max: 50, message: '长度不能超过50个字符', trigger: 'blur' }
        ],
        bridgeType: [
          { required: true, message: '请选择桥梁类型', trigger: 'change' }
        ],
        buildTime: [
          { required: true, message: '请选择建成时间', trigger: 'change' }
        ],
        clearanceHeight: [
          { required: true, message: '请输入净空高度', trigger: 'blur' },
          { type: 'number', message: '净空高度必须为数字', trigger: 'blur' }
        ],
        warningHeight: [
          { required: true, message: '请输入预警高度', trigger: 'blur' },
          { type: 'number', message: '预警高度必须为数字', trigger: 'blur' }
        ],
        warningSpeed: [
          { required: true, message: '请输入预警速度', trigger: 'blur' },
          { type: 'number', message: '预警速度必须为数字', trigger: 'blur' }
        ],
        status: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ]
      }
    };
  },
  watch: {
    value: {
      handler(newValue) {
        // 通过深拷贝避免对象引用问题，防止循环更新
        this.form = JSON.parse(JSON.stringify(newValue));
      },
      deep: true,
      immediate: true
    },
    form: {
      handler(newValue) {
        // 避免向父组件发送不必要的更新
        if (JSON.stringify(newValue) !== JSON.stringify(this.value)) {
          this.$emit('input', newValue);
        }
      },
      deep: true
    }
  },
  methods: {
    // 验证表单
    validate() {
      return new Promise(resolve => {
        this.$refs.bridgeForm.validate(valid => {
          if (valid) {
            if (!this.form.latitude || !this.form.longitude) {
              this.$message.error('请标记桥梁位置');
              resolve(false);
            } else {
              resolve(true);
            }
          } else {
            resolve(false);
          }
        });
      });
    },
    
    // 重置表单
    resetForm() {
      this.$refs.bridgeForm.resetFields();
    },
    
    // 格式化经度
    formatLongitude,
    
    // 格式化纬度
    formatLatitude
  },
  computed: {
    actualBridgeTypeOptions() {
      return this.bridgeTypeOptions.length > 0 ? this.bridgeTypeOptions : this.localBridgeTypeOptions;
    }
  }
};
</script>

<style scoped>
.active-location-btn {
  background-color: #409eff;
  color: white;
  border-color: #409eff;
}

.coord-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}
</style> 